import { useCallback, useEffect, useRef, useState } from "react";
import { useHandleSuccessAuthentication } from "./useHandleSuccessAuthentication";

interface UseEmailVerificationPasswordStateProps {
  error?: string | null;
  isSubmitting?: boolean;
  onPasswordSubmit?: (password: string) => void;
}

export const useEmailVerificationPasswordState = ({
  isSubmitting = false,
  error = null,
  onPasswordSubmit,
}: UseEmailVerificationPasswordStateProps) => {
  const [password, setPassword] = useState("");
  const [isSuccessShown, setIsSuccessShown] = useState(false);
  const wasSubmittingRef = useRef(false);
  const { reloadPage } = useHandleSuccessAuthentication();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!password.trim()) {
        return;
      }

      onPasswordSubmit?.(password);
    },
    [password, onPasswordSubmit],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !isSubmitting) {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(e as any);
      }
    },
    [isSubmitting, handleSubmit],
  );

  useEffect(() => {
    if (isSubmitting) {
      wasSubmittingRef.current = true;
      setIsSuccessShown(false);
    } else if (wasSubmittingRef.current && !isSubmitting) {
      wasSubmittingRef.current = false;

      if (!error) {
        setIsSuccessShown(true);
      }
    }
  }, [isSubmitting, error]);

  useEffect(() => {
    if (error) {
      setIsSuccessShown(false);
    }
  }, [error]);

  useEffect(() => {
    if (isSuccessShown) reloadPage();
  }, [isSuccessShown]);

  return {
    password,
    hasError: !!error,
    isSuccessShown,
    handleSubmit,
    handlePasswordChange,
    handleKeyDown,
  };
};
